import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { X, CheckCircle, ShieldCheck, Heart, Loader2, Printer } from "lucide-react";
import { generateClient } from "aws-amplify/api";
import { getSlumSoccerFundraisers } from "../../graphql/queries";
import { updateSlumSoccerFundraisers } from "../../graphql/mutations";
import { PAYMENT_CONFIG } from "../../config/payment";

const client = generateClient();

export function DonationModal({
  isOpen,
  onClose,
  fundraiserId,
  fundraiserTitle = "Fundraiser",
  paymentFor = "TEAM",
  participantId = null,
  participantName = "",
}) {
  const [selectedPreset, setSelectedPreset] = useState(1000);
  const [customAmount, setCustomAmount] = useState("");
  const [formData, setFormData] = useState({
    donorName: "",
    donorEmail: "",
    donorMobNo: "",
    donorPanNo: "",
    donorAddress: "",
    is80GRequired: true,
  });

  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [successData, setSuccessData] = useState(null);
  const [cancelled, setCancelled] = useState(false);
  const didDonate = useRef(false);

  if (!isOpen) return null;

  const currentAmount = customAmount ? parseFloat(customAmount) : selectedPreset;

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    setCustomAmount("");
    if (formErrors.amount) {
      setFormErrors((prev) => ({ ...prev, amount: null }));
    }
  };

  const handleCustomAmountChange = (e) => {
    const val = e.target.value;
    setCustomAmount(val);
    setSelectedPreset(null);
    if (formErrors.amount) {
      setFormErrors((prev) => ({ ...prev, amount: null }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let formattedVal = value;
    if (name === "donorPanNo") {
      formattedVal = value.toUpperCase();
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : formattedVal,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const persistRaisedAmount = async (amount) => {
    const response = await client.graphql({
      query: getSlumSoccerFundraisers,
      variables: { fundraiserId },
    });

    const fresh = response.data.getSlumSoccerFundraisers;
    if (!fresh) return;

    if (paymentFor === "PARTICIPANT") {
      let participants = [];
      if (Array.isArray(fresh.participants)) {
        participants = fresh.participants;
      } else if (typeof fresh.participants === "string") {
        try {
          const parsed = JSON.parse(fresh.participants);
          participants = Array.isArray(parsed) ? parsed : [];
        } catch {
          participants = [];
        }
      }

      const updatedParticipants = participants.map((p) =>
        p.slug === participantId
          ? { ...p, raised: (Number(p.raised) || 0) + amount }
          : p
      );

      await client.graphql({
        query: updateSlumSoccerFundraisers,
        variables: {
          input: {
            fundraiserId,
            participants: JSON.stringify(updatedParticipants),
          },
        },
      });
    } else {
      const newRaised = (Number(fresh.raisedAmount) || 0) + amount;
      await client.graphql({
        query: updateSlumSoccerFundraisers,
        variables: {
          input: { fundraiserId, raisedAmount: newRaised },
        },
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!currentAmount || isNaN(currentAmount) || currentAmount < 10) {
      errors.amount = "Please enter a valid donation amount (minimum ₹10).";
    }
    if (!formData.donorName.trim()) {
      errors.donorName = "Full Name is required.";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.donorEmail.trim()) {
      errors.donorEmail = "Email address is required.";
    } else if (!emailRegex.test(formData.donorEmail.trim())) {
      errors.donorEmail = "Please enter a valid email address.";
    }
    const phoneRegex = /^[0-9]{10}$/;
    if (!formData.donorMobNo.trim()) {
      errors.donorMobNo = "Mobile number is required.";
    } else if (!phoneRegex.test(formData.donorMobNo.trim().replace(/\D/g, ""))) {
      errors.donorMobNo = "Please enter a valid 10-digit mobile number.";
    }
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!formData.donorPanNo.trim()) {
      errors.donorPanNo = "PAN number is mandatory for NGO donation records.";
    } else if (!panRegex.test(formData.donorPanNo.trim().toUpperCase())) {
      errors.donorPanNo = "Please enter a valid 10-digit PAN (e.g. ABCDE1234F).";
    }
    if (!formData.donorAddress.trim()) {
      errors.donorAddress = "Address is required.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (!window.Razorpay) {
      alert("Payment gateway SDK is loading. Please check your connection and try again.");
      return;
    }

    setLoading(true);
    setLoadingText("Initializing donation...");

    try {
      // 1. Call createOrder Lambda
      const payload = {
        amount: currentAmount,
        fundraiserId: fundraiserId || "slum-soccer-general",
        paymentFor: paymentFor || "TEAM",
        participantId: paymentFor === "PARTICIPANT" ? participantId : null,
        donorName: formData.donorName.trim(),
        donorEmail: formData.donorEmail.trim(),
        donorMobNo: formData.donorMobNo.trim(),
        donorPanNo: formData.donorPanNo.trim().toUpperCase(),
        donorAddress: formData.donorAddress.trim(),
        is80GRequired: Boolean(formData.is80GRequired),
      };

      const createRes = await fetch(PAYMENT_CONFIG.CREATE_ORDER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!createRes.ok) {
        const errData = await createRes.json().catch(() => ({}));
        throw new Error(errData.error || `Failed to create order (Status ${createRes.status})`);
      }

      const orderData = await createRes.json();

      // 2. Configure Razorpay checkout options
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency || "INR",
        name: PAYMENT_CONFIG.ORG_NAME,
        description:
          paymentFor === "PARTICIPANT" && participantName
            ? `Donation for Participant: ${participantName}`
            : `Donation for ${fundraiserTitle}`,
        order_id: orderData.id,
        prefill: {
          name: formData.donorName,
          email: formData.donorEmail,
          contact: formData.donorMobNo,
        },
        theme: {
          color: "#FF6600",
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setLoadingText("");
            setCancelled(true);
          },
        },
        handler: async (response) => {
          setLoading(true);
          setLoadingText("Verifying your payment securely...");

          try {
            // 3. Call verifyOrder Lambda
            const verifyRes = await fetch(PAYMENT_CONFIG.VERIFY_ORDER_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });

            const verifyData = await verifyRes.json();

            if (!verifyRes.ok || !verifyData.success) {
              throw new Error(verifyData.error || "Payment verification failed. Please contact support.");
            }

            try {
              await persistRaisedAmount(currentAmount);
            } catch (persistError) {
              console.error("Failed to update raised amount:", persistError);
            }

            didDonate.current = true;
            setLoading(false);
            setSuccessData({
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              amount: currentAmount,
              donorName: formData.donorName,
              donorEmail: formData.donorEmail,
              donorPanNo: formData.donorPanNo.toUpperCase(),
              fundraiserTitle:
                paymentFor === "PARTICIPANT" && participantName
                  ? `${fundraiserTitle} (${participantName})`
                  : fundraiserTitle,
              date: new Date().toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              }),
            });
          } catch (verifyError) {
            console.error("Verification error:", verifyError);
            setLoading(false);
            alert(`Payment verification note: ${verifyError.message}`);
          }
        },
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on("payment.failed", function (response) {
        console.error("Payment failed:", response.error);
        setLoading(false);
        // Razorpay already shows the failure UI inside its modal — no alert needed here.
      });

      razorpayInstance.open();
    } catch (err) {
      console.error("Payment initialization error:", err);
      setLoading(false);
      setLoadingText("");
      alert(`Could not initiate payment: ${err.message}`);
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleCloseModal = () => {
    setSuccessData(null);
    setCancelled(false);
    setLoading(false);
    onClose();
    if (didDonate.current) {
      didDonate.current = false;
      window.location.reload();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden my-8 border border-gray-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#FF6600] to-[#E65100] px-6 py-5 text-white flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Heart className="w-5 h-5 text-yellow-300 fill-yellow-300" />
              <span className="text-xs uppercase tracking-wider font-bold text-yellow-200">
                {paymentFor === "PARTICIPANT" ? "Participant Support" : "Team Fundraiser"}
              </span>
            </div>
            <h3 className="text-xl font-bold leading-tight">
              {paymentFor === "PARTICIPANT" && participantName
                ? `Support ${participantName}`
                : `Donate to ${fundraiserTitle}`}
            </h3>
          </div>
          <button
            type="button"
            onClick={handleCloseModal}
            className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10 transition"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Cancelled View */}
        {cancelled ? (
          <div className="p-6 md:p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-9 h-9" />
            </div>
            <h4 className="text-xl font-extrabold text-gray-800 mb-2">Payment Cancelled</h4>
            <p className="text-gray-500 text-sm mb-6">
              You closed the payment window. Your donation was not processed and no amount was charged.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={() => setCancelled(false)}
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg bg-[#FF6600] text-white hover:bg-[#E65100] font-semibold text-sm transition shadow-md"
              >
                Try Again
              </button>
              <button
                type="button"
                onClick={handleCloseModal}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium text-sm transition"
              >
                Close
              </button>
            </div>
          </div>
        ) : successData ? (
          <div className="p-6 md:p-8 text-center" id="donation-receipt-view">
            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10" />
            </div>
            <h4 className="text-2xl font-extrabold text-gray-900 mb-1">Thank You for Your Support!</h4>
            <p className="text-gray-600 text-sm mb-6">
              Your contribution makes a real difference in the lives of our players.
            </p>

            {/* Receipt card */}
            <div className="bg-orange-50/70 border border-orange-200/70 rounded-xl p-4 text-left mb-6 text-sm">
              <div className="flex justify-between py-1.5 border-b border-orange-200/50">
                <span className="text-gray-500 font-medium">Receipt Ref / Order ID</span>
                <span className="font-mono font-semibold text-gray-800">{successData.orderId}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-orange-200/50">
                <span className="text-gray-500 font-medium">Payment ID</span>
                <span className="font-mono font-semibold text-gray-800">{successData.paymentId}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-orange-200/50">
                <span className="text-gray-500 font-medium">Amount Donated</span>
                <span className="text-lg font-bold text-[#FF6600]">₹{successData.amount.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-orange-200/50">
                <span className="text-gray-500 font-medium">Donor Name</span>
                <span className="font-semibold text-gray-800">{successData.donorName}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-orange-200/50">
                <span className="text-gray-500 font-medium">PAN</span>
                <span className="font-mono font-semibold text-gray-800">{successData.donorPanNo}</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-gray-500 font-medium">Date</span>
                <span className="font-medium text-gray-800">{successData.date}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={handlePrintReceipt}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium text-sm transition"
              >
                <Printer className="w-4 h-4" /> Print / Save Receipt
              </button>
              <button
                type="button"
                onClick={handleCloseModal}
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg bg-[#FF6600] text-white hover:bg-[#E65100] font-semibold text-sm transition shadow-md"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* Donation Form */
          <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-5">
            {/* Amount Selection */}
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">Select Donation Amount</label>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {PAYMENT_CONFIG.DEFAULT_AMOUNTS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => handlePresetSelect(preset)}
                    className={`py-2 px-1 text-center font-bold text-sm rounded-lg border transition ${
                      selectedPreset === preset && !customAmount
                        ? "bg-[#FF6600] text-white border-[#FF6600] shadow-sm"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-orange-50 hover:border-orange-300"
                    }`}
                  >
                    ₹{preset.toLocaleString("en-IN")}
                  </button>
                ))}
              </div>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center font-bold text-gray-500">₹</span>
                <input
                  type="number"
                  placeholder="Or enter custom amount"
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  min="10"
                  className={`w-full pl-8 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600] ${
                    formErrors.amount ? "border-red-500 bg-red-50/30" : "border-gray-300"
                  }`}
                />
              </div>
              {formErrors.amount && <p className="text-red-500 text-xs mt-1 font-medium">{formErrors.amount}</p>}
            </div>

            {/* Donor Details */}
            <div className="space-y-3 pt-2 border-t border-gray-100">
              <p className="text-xs uppercase font-bold text-gray-500 tracking-wider">Donor Details</p>

              {/* Full Name */}
              <div>
                <input
                  type="text"
                  name="donorName"
                  placeholder="Full Name *"
                  value={formData.donorName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600] ${
                    formErrors.donorName ? "border-red-500 bg-red-50/30" : "border-gray-300"
                  }`}
                />
                {formErrors.donorName && <p className="text-red-500 text-xs mt-1 font-medium">{formErrors.donorName}</p>}
              </div>

              {/* Email & Mobile */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <input
                    type="email"
                    name="donorEmail"
                    placeholder="Email Address *"
                    value={formData.donorEmail}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600] ${
                      formErrors.donorEmail ? "border-red-500 bg-red-50/30" : "border-gray-300"
                    }`}
                  />
                  {formErrors.donorEmail && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{formErrors.donorEmail}</p>
                  )}
                </div>
                <div>
                  <input
                    type="tel"
                    name="donorMobNo"
                    placeholder="10-digit Mobile No *"
                    maxLength="10"
                    value={formData.donorMobNo}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600] ${
                      formErrors.donorMobNo ? "border-red-500 bg-red-50/30" : "border-gray-300"
                    }`}
                  />
                  {formErrors.donorMobNo && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{formErrors.donorMobNo}</p>
                  )}
                </div>
              </div>

              {/* PAN Number */}
              <div>
                <input
                  type="text"
                  name="donorPanNo"
                  placeholder="PAN Number * (e.g. ABCDE1234F)"
                  maxLength="10"
                  value={formData.donorPanNo}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-sm border uppercase font-mono tracking-wider rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600] ${
                    formErrors.donorPanNo ? "border-red-500 bg-red-50/30" : "border-gray-300"
                  }`}
                />
                {formErrors.donorPanNo && <p className="text-red-500 text-xs mt-1 font-medium">{formErrors.donorPanNo}</p>}
                <p className="text-[11px] text-gray-500 mt-1">
                  Mandatory for all NGO donations under Indian Income Tax regulations.
                </p>
              </div>

              {/* Address */}
              <div>
                <textarea
                  name="donorAddress"
                  rows="2"
                  placeholder="Full Residential / Postal Address *"
                  value={formData.donorAddress}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600] ${
                    formErrors.donorAddress ? "border-red-500 bg-red-50/30" : "border-gray-300"
                  }`}
                />
                {formErrors.donorAddress && (
                  <p className="text-red-500 text-xs mt-1 font-medium">{formErrors.donorAddress}</p>
                )}
              </div>

              {/* 80G Tax Exemption Checkbox */}
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="is80GRequired"
                  name="is80GRequired"
                  checked={formData.is80GRequired}
                  onChange={handleChange}
                  className="w-4 h-4 text-[#FF6600] border-gray-300 rounded focus:ring-[#FF6600] accent-[#FF6600]"
                />
                <label htmlFor="is80GRequired" className="text-xs text-gray-700 font-medium cursor-pointer">
                  I require an 80G Tax Exemption Receipt (50% Tax Relief)
                </label>
              </div>
            </div>

            {/* Submit CTA */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-6 bg-[#FF6600] hover:bg-[#E65100] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{loadingText || "Processing..."}</span>
                  </>
                ) : (
                  <>
                    <span>Proceed to Donate ₹{currentAmount ? currentAmount.toLocaleString("en-IN") : "0"}</span>
                  </>
                )}
              </button>

              <div className="flex items-center justify-center gap-1.5 mt-3 text-xs text-gray-500">
                <ShieldCheck className="w-4 h-4 text-green-600" />
                <span>Secured 256-bit payment via Razorpay</span>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}
